import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Pencil, Trash2, X } from 'lucide-react';
import apiService from '../../../apiService';
import { fmtMoney, fmtInt, fmtDate, Card, KpiCard, Spinner, usePeriodControls } from '../financeShared';

const emptyForm = (firstCat) => ({ category: firstCat || '', label: '', amount: '', date: new Date().toISOString().slice(0, 10), payment_method: '', recurring: false, note: '' });

export default function ExpensesTab({ currency, rate }) {
  const { params, qs, controls } = usePeriodControls('1year', 'month');
  const [catFilter, setCatFilter] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const fmt = (n) => fmtMoney(n, currency, rate);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams(qs());
      if (catFilter) p.set('category', catFilter);
      const res = await apiService.get(`/admin/finance/expenses?${p}`, { userType: 'admin' });
      if (res?.success) setData(res);
    } catch (e) { console.error('expenses:', e); } finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params, catFilter]);
  useEffect(() => { load(); }, [load]);

  const categories = data?.categories || [];
  const labelMap = Object.fromEntries(categories.map(c => [c.key, c.label]));
  const labelFor = (key) => labelMap[key] || key;

  const openCreate = () => { setForm(emptyForm(categories[0]?.key)); setEditId(null); setShowForm(true); };
  const openEdit = (e) => { setForm({ category: e.category, label: e.label, amount: String(e.amount), date: new Date(e.date).toISOString().slice(0, 10), payment_method: e.payment_method || '', recurring: !!e.recurring, note: e.note || '' }); setEditId(e.expense_id); setShowForm(true); };

  const save = async () => {
    if (!form.label.trim() || !form.amount || !form.category) return;
    setSaving(true);
    try {
      const body = { ...form, amount: Number(form.amount) };
      if (editId) await apiService.patch(`/admin/finance/expenses/${editId}`, body, { userType: 'admin' });
      else await apiService.post('/admin/finance/expenses', body, { userType: 'admin' });
      setShowForm(false); load();
    } catch (e) { console.error('save expense:', e); alert('Erreur lors de l\'enregistrement'); } finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm('Supprimer cette dépense ?')) return;
    try { await apiService.delete(`/admin/finance/expenses/${id}`, { userType: 'admin' }); load(); }
    catch (e) { console.error('delete expense:', e); }
  };

  const expenses = data?.expenses || [];
  const byCategory = data?.byCategory || [];

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '0.5rem' }}>
        {controls({ showGroupBy: false })}
        <button className="btn-primary" onClick={openCreate} style={{ padding: '0.45rem 1rem', display: 'flex', alignItems: 'center', gap: 6, marginBottom: '1.25rem' }}><Plus size={16} /> Ajouter une dépense</button>
      </div>

      <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap', marginBottom: '1.5rem', alignItems: 'center' }}>
        <button onClick={() => setCatFilter('')} style={{ padding: '0.35rem 0.85rem', borderRadius: '8px', border: 'none', cursor: 'pointer', fontSize: '0.8rem', fontWeight: catFilter === '' ? 700 : 600, background: catFilter === '' ? '#1E2229' : '#f0f4f8', color: catFilter === '' ? 'white' : '#414852' }}>Toutes</button>
        {categories.map(c => (
          <button key={c.key} onClick={() => setCatFilter(c.key)} style={{ padding: '0.35rem 0.85rem', borderRadius: '8px', border: 'none', cursor: 'pointer', fontSize: '0.8rem', fontWeight: catFilter === c.key ? 700 : 600, background: catFilter === c.key ? '#1E2229' : '#f0f4f8', color: catFilter === c.key ? 'white' : '#414852' }}>{c.label}</button>
        ))}
        <span style={{ fontSize: '0.72rem', color: '#9AA0A8', marginLeft: 'auto' }}>Les catégories se gèrent dans l'onglet <strong>Paramètres</strong>.</span>
      </div>

      {loading ? <Spinner /> : (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
            <KpiCard label="Total dépenses (période)" value={fmt(data?.totalAmount || 0)} color="#dc2626" border="#dc2626" sub={`${fmtInt(data?.total || 0)} ligne(s)`} />
            {byCategory.slice(0, 3).map(c => (
              <KpiCard key={c.category} label={c.label || labelFor(c.category)} value={fmt(c.total)} color="#414852" border="#9AA0A8" sub={`${fmtInt(c.count)}×`} />
            ))}
          </div>

          <Card style={{ padding: 0, overflow: 'hidden' }}>
            {expenses.length === 0 ? <p style={{ color: '#9AA0A8', padding: '2rem' }}>Aucune dépense. Cliquez sur « Ajouter une dépense ».</p> : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead><tr style={{ background: '#FFFFFF', borderBottom: '2px solid #E9EBEF' }}>
                    {['Date', 'Catégorie', 'Libellé', 'Montant', ''].map(h => (
                      <th key={h} style={{ padding: '0.7rem 1rem', textAlign: h === 'Montant' ? 'right' : 'left', fontWeight: 700, color: '#414852', fontSize: '0.75rem', textTransform: 'uppercase' }}>{h}</th>
                    ))}
                  </tr></thead>
                  <tbody>
                    {expenses.map((e, idx) => (
                      <tr key={e.expense_id} style={{ borderBottom: '1px solid #E9EBEF', background: idx % 2 === 0 ? '#fff' : '#FFFFFF' }}>
                        <td style={{ padding: '0.6rem 1rem', color: '#5C636E', fontSize: '0.82rem', whiteSpace: 'nowrap' }}>{fmtDate(e.date)}</td>
                        <td style={{ padding: '0.6rem 1rem' }}><span style={{ fontSize: '0.72rem', padding: '0.15rem 0.5rem', borderRadius: '10px', background: '#F4F5F7', color: '#414852', fontWeight: 600 }}>{labelFor(e.category)}</span>{e.recurring && <span style={{ marginLeft: 4, fontSize: '0.65rem', color: '#FF7800' }}>↻</span>}</td>
                        <td style={{ padding: '0.6rem 1rem', color: '#1E2229', fontSize: '0.85rem' }}>{e.label}{e.note ? <span style={{ color: '#9AA0A8', fontSize: '0.75rem' }}> — {e.note}</span> : ''}</td>
                        <td style={{ padding: '0.6rem 1rem', textAlign: 'right', fontWeight: 700, color: '#dc2626', whiteSpace: 'nowrap' }}>{fmt(e.amount)}</td>
                        <td style={{ padding: '0.6rem 1rem', textAlign: 'right', whiteSpace: 'nowrap' }}>
                          <button onClick={() => openEdit(e)} title="Modifier" style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#FF7800', marginRight: 8 }}><Pencil size={15} /></button>
                          <button onClick={() => remove(e.expense_id)} title="Supprimer" style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#dc2626' }}><Trash2 size={15} /></button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </>
      )}

      {showForm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={() => setShowForm(false)}>
          <div style={{ background: 'white', borderRadius: '14px', padding: '1.75rem', width: '100%', maxWidth: 480, maxHeight: '90vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
              <h3 style={{ margin: 0, color: '#1E2229' }}>{editId ? 'Modifier la dépense' : 'Nouvelle dépense'}</h3>
              <button onClick={() => setShowForm(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9AA0A8' }}><X size={20} /></button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.9rem' }}>
              <label style={{ fontSize: '0.8rem', fontWeight: 600, color: '#414852' }}>Catégorie
                <select value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))} style={{ width: '100%', marginTop: 4, padding: '0.5rem', borderRadius: '6px', border: '1px solid #E9EBEF' }}>
                  {categories.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
                </select>
              </label>
              <label style={{ fontSize: '0.8rem', fontWeight: 600, color: '#414852' }}>Libellé
                <input value={form.label} onChange={e => setForm(f => ({ ...f, label: e.target.value }))} placeholder="Ex. Salaire dev janvier" style={{ width: '100%', marginTop: 4, padding: '0.5rem', borderRadius: '6px', border: '1px solid #E9EBEF' }} />
              </label>
              <div style={{ display: 'flex', gap: '0.75rem' }}>
                <label style={{ fontSize: '0.8rem', fontWeight: 600, color: '#414852', flex: 1 }}>Montant (CDF)
                  <input type="number" value={form.amount} onChange={e => setForm(f => ({ ...f, amount: e.target.value }))} style={{ width: '100%', marginTop: 4, padding: '0.5rem', borderRadius: '6px', border: '1px solid #E9EBEF' }} />
                </label>
                <label style={{ fontSize: '0.8rem', fontWeight: 600, color: '#414852', flex: 1 }}>Date
                  <input type="date" value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))} style={{ width: '100%', marginTop: 4, padding: '0.5rem', borderRadius: '6px', border: '1px solid #E9EBEF' }} />
                </label>
              </div>
              <label style={{ fontSize: '0.8rem', fontWeight: 600, color: '#414852' }}>Note (optionnel)
                <input value={form.note} onChange={e => setForm(f => ({ ...f, note: e.target.value }))} style={{ width: '100%', marginTop: 4, padding: '0.5rem', borderRadius: '6px', border: '1px solid #E9EBEF' }} />
              </label>
              <label style={{ fontSize: '0.8rem', fontWeight: 600, color: '#414852', display: 'flex', alignItems: 'center', gap: 8 }}>
                <input type="checkbox" checked={form.recurring} onChange={e => setForm(f => ({ ...f, recurring: e.target.checked }))} /> Dépense récurrente
              </label>
              <button className="btn-primary" onClick={save} disabled={saving || !form.label.trim() || !form.amount || !form.category} style={{ padding: '0.6rem', marginTop: '0.5rem' }}>
                {saving ? 'Enregistrement…' : (editId ? 'Enregistrer' : 'Ajouter')}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
